import { Node } from '@tiptap/core';

declare module '@tiptap/core' {
    interface Storage {
        externalEmbed: { onEdit: ((position: number) => void) | null };
    }
}

export interface ExternalEmbedAttrs {
    src: string;
    title: string;
    width: string;
    height: number;
    align: 'left' | 'center' | 'right';
    fullscreen: boolean;
}

export const defaultEmbed: ExternalEmbedAttrs = {
    src: '', title: 'External content', width: '100%', height: 380, align: 'center', fullscreen: true,
};

export function validateEmbedUrl(value: string): string {
    const src = value.trim();
    try {
        const url = new URL(src);
        if (!/^https?:$/.test(url.protocol) || url.username || url.password || /[\s\\]/.test(src) || Array.from(src).some(char => char.charCodeAt(0) < 32)) throw new Error();
    } catch { throw new Error('Enter a complete HTTP or HTTPS embed URL.'); }
    return src;
}

export function validateEmbedWidth(value: string): string {
    const width = value.trim();
    if (/^\d+(?:\.\d+)?%$/.test(width) && parseFloat(width) > 0 && parseFloat(width) <= 100) return width;
    if (/^\d+(?:px)?$/.test(width) && parseInt(width, 10) > 0 && parseInt(width, 10) <= 10000) return String(parseInt(width, 10));
    throw new Error('Width must be a positive pixel value or a percentage up to 100%.');
}

function readIframe(iframe: Element, wrapper?: Element): ExternalEmbedAttrs {
    const width = iframe.getAttribute('width') || (iframe as HTMLElement).style.width || defaultEmbed.width;
    const height = Number(iframe.getAttribute('height') || defaultEmbed.height);
    if (!Number.isInteger(height) || height < 20 || height > 10000) throw new Error('Height must be between 20 and 10000 pixels.');
    const alignment = wrapper?.getAttribute('data-align');
    return { src: validateEmbedUrl(iframe.getAttribute('src') || ''),
        title: iframe.getAttribute('title') || defaultEmbed.title,
        width: validateEmbedWidth(width), height,
        align: alignment === 'left' || alignment === 'right' ? alignment : 'center',
        fullscreen: iframe.hasAttribute('allowfullscreen') || /\bfullscreen\b/i.test(iframe.getAttribute('allow') || ''),
    };
}

export function parseEmbedSnippet(value: string): ExternalEmbedAttrs {
    const doc = new DOMParser().parseFromString(value, 'text/html');
    const iframe = doc.body.firstElementChild;
    if (!iframe || iframe.tagName !== 'IFRAME' || doc.body.children.length !== 1 ||
        Array.from(doc.body.childNodes).some(node => node.nodeType === 3 && node.textContent?.trim()) ||
        doc.querySelector('script') || iframe.hasAttribute('srcdoc')) {
        throw new Error('Paste a single iframe embed snippet with a src URL.');
    }
    return readIframe(iframe);
}

function iframeAttributes(attrs: ExternalEmbedAttrs) {
    return { src: validateEmbedUrl(attrs.src), title: attrs.title, width: attrs.width, height: attrs.height,
        style: `display:block;width:${attrs.width.endsWith('%') ? attrs.width : `${attrs.width}px`};max-width:100%;border:0;margin-left:${attrs.align === 'left' ? '0' : 'auto'};margin-right:${attrs.align === 'right' ? '0' : 'auto'}`,
        ...(attrs.fullscreen ? { allowfullscreen: '', allow: 'fullscreen' } : {}),
    };
}

export const ExternalEmbedNode = Node.create({
    name: 'externalEmbed', group: 'block', atom: true, draggable: true,
    addStorage() { return { onEdit: null as ((position: number) => void) | null }; },
    addAttributes() {
        // Parse these together in readIframe, including dimensions and booleans.
        return Object.fromEntries(Object.entries(defaultEmbed).map(([name, value]) => [name, { default: value, rendered: false, parseHTML: () => null }]));
    },
    parseHTML() {
        return [
            { tag: 'div[data-external-embed]', getAttrs: element => {
                try { const iframe = element.querySelector('iframe'); return iframe ? readIframe(iframe, element) : false; }
                catch { return false; }
            } },
            { tag: 'iframe[src]', priority: 40, getAttrs: element => {
                try { return element.hasAttribute('srcdoc') ? false : readIframe(element); } catch { return false; }
            } },
        ];
    },
    renderHTML({ node }) {
        const attrs = node.attrs as ExternalEmbedAttrs;
        return ['div', { 'data-external-embed': '', 'data-align': attrs.align }, ['iframe', iframeAttributes(attrs)]];
    },
    addNodeView() {
        return ({ node, editor, getPos }) => {
            const doc = (editor.options.element as HTMLElement | null)?.ownerDocument ?? document;
            const dom = doc.createElement('div');
            dom.contentEditable = 'false';
            dom.className = 'external-embed-editor';
            const toolbar = doc.createElement('div');
            toolbar.style.cssText = 'display:flex;gap:8px;margin-bottom:6px';
            const edit = doc.createElement('button');
            edit.type = 'button'; edit.textContent = 'Edit external content…';
            edit.onclick = event => { event.preventDefault(); const pos = getPos(); if (typeof pos === 'number') this.storage.onEdit?.(pos); };
            const remove = doc.createElement('button');
            remove.type = 'button'; remove.textContent = 'Delete';
            remove.onclick = event => { event.preventDefault(); const pos = getPos(); if (typeof pos === 'number') editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run(); };
            toolbar.append(edit, remove);
            const iframe = doc.createElement('iframe');
            Object.entries(iframeAttributes(node.attrs as ExternalEmbedAttrs)).forEach(([key, value]) => iframe.setAttribute(key, String(value)));
            dom.append(toolbar, iframe);
            return { dom, stopEvent: event => toolbar.contains(event.target as globalThis.Node), ignoreMutation: () => true };
        };
    },
});
